import { useRef, useState, useEffect } from "react";
import { useIsMobileBreakpoint } from "../../../../hooks/useIsMobileBreakpoint";
import { Swiper, SwiperSlide } from "swiper/react";
import RoundPhaseWrapper from "../roundPhaseWrapper";
import Final from "../final/final";
import ThirdPlace from "../thirdPlace/thirdPlace";
import Champion from "../champion/champion";
import { R32_DISPLAY_ORDER, R16_DISPLAY_ORDER, reorderByMatchNumber } from "../qualifyingRoundsPanel";
import "../../qualifyingRounds/qualifyingRoundsPanel.sass";
import "swiper/css";

export default function QualifyingRoundsPanelMobile({ qualifyingState, onSelectWinner, captureRef, scrollToChampion, onScrollComplete }) {
  const isSmallMobile = useIsMobileBreakpoint(400);
  const slidesPerView = isSmallMobile ? 1.6 : 2;
  const swiperRef = useRef(null);
  const panelRef = useRef(null);
  const [navState, setNavState] = useState({ isBeginning: true, isEnd: false });
  const [isDragging, setIsDragging] = useState(false);
  const [containerHeight, setContainerHeight] = useState(null);
  const slideContentRefs = useRef({});

  const updateHeightFromSwiper = (swiper) => {
    if (slideContentRefs.current[swiper.activeIndex]) {
      const height = slideContentRefs.current[swiper.activeIndex].offsetHeight;
      setContainerHeight(height);
    }
  };

  const rounds = [
    { id: "r32", title: "DIECISEISAVOS", type: "phase", strategy: "quads", className: "RoundOf32", matches: reorderByMatchNumber(qualifyingState.roundOf32, R32_DISPLAY_ORDER) },
    { id: "r16", title: "OCTAVOS", type: "phase", strategy: "octets", className: "RoundOf16", matches: reorderByMatchNumber(qualifyingState.roundOf16, R16_DISPLAY_ORDER) },
    { id: "r8", title: "CUARTOS", type: "phase", strategy: "pairs", className: "RoundOf8", matches: qualifyingState.roundOf8 },
    { id: "sf-tp", title: "Semifinal y 3er Lugar", type: "combined" },
    { id: "f", title: "Final", type: "final", match: qualifyingState.final?.[0] },
    { id: "c", title: "Campeón", type: "champion", winner: qualifyingState.final?.[0]?.winner },
  ];

  // Auto-slide al campeón en mobile cuando se simula
  useEffect(() => {
    if (scrollToChampion && swiperRef.current) {
      // El slide del campeón es el último (index = rounds.length - 1)
      const championIndex = rounds.length - 1;
      requestAnimationFrame(() => {
        swiperRef.current.slideTo(championIndex, 600);
        onScrollComplete?.();
      });
    }
  }, [scrollToChampion, onScrollComplete]);

  return (
    <div className="QualifyingRoundsPanel" ref={panelRef}>
      <Swiper
        className="bracket-swiper"
        style={{ overflowY: 'hidden' }}
        autoHeight={true}
        slidesPerView={slidesPerView}
        spaceBetween={0}
        speed={300}
        loop={false}
        watchOverflow
        resistance
        resistanceRatio={0.85}
        threshold={5}
        touchEventsTarget="wrapper"
        simulateTouch
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setNavState({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd });
          updateHeightFromSwiper(swiper);
        }}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => setIsDragging(false)}
        onProgress={(swiper) => updateHeightFromSwiper(swiper)}
        onSetTranslate={(swiper) => updateHeightFromSwiper(swiper)}
        onSlideChange={(swiper) => {
          setNavState({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd });
        }}
        onReachBeginning={(swiper) =>
          setNavState({ isBeginning: true, isEnd: swiper.isEnd })
        }
        onReachEnd={(swiper) =>
          setNavState({ isBeginning: swiper.isBeginning, isEnd: true })
        }
      >
        {rounds.map((round, idx) => {
          if (round.type === "combined") {
            return (
              <SwiperSlide key={round.id} className="round-slide">
                <div
                  className="round-viewport"
                  style={{
                    height: containerHeight ? `${containerHeight}px` : "auto",
                    transition: isDragging ? "none" : "height 240ms ease",
                  }}
                >
                  <div
                    className="round"
                    ref={(el) => (slideContentRefs.current[idx] = el)}
                  >
                    <div className="round-list">
                      <div className="SemiFinalsAndThirdPlace">
                        <RoundPhaseWrapper
                          title="SEMIFINALES"
                          matches={qualifyingState.semiFinals || []}
                          onSelectWinner={onSelectWinner}
                          className="SemiFinals"
                          groupingStrategy="none"
                        />
                        <ThirdPlace
                          match={qualifyingState.thirdPlace?.[0]}
                          onSelectWinner={onSelectWinner}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          }

          if (round.type === "champion") {
            return (
              <SwiperSlide key={round.id} className="round-slide">
                <div
                  className="round-viewport"
                  style={{
                    height: containerHeight ? `${containerHeight}px` : "auto",
                    transition: isDragging ? "none" : "height 240ms ease",
                  }}
                >
                  <div className="round" ref={(el) => (slideContentRefs.current[idx] = el)}>
                    <div className="round-list">
                      <Champion winner={round.winner} qualifyingState={qualifyingState} />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          }

          if (round.type === "final") {
            return (
              <SwiperSlide key={round.id} className="round-slide">
                <div
                  className="round-viewport"
                  style={{
                    height: containerHeight ? `${containerHeight}px` : "auto",
                    transition: isDragging ? "none" : "height 240ms ease",
                  }}
                >
                  <div
                    className="round"
                    ref={(el) => (slideContentRefs.current[idx] = el)}
                  >
                    <div className="round-list">
                      <Final
                        match={round.match}
                        onSelectWinner={onSelectWinner}
                      />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          }

          return (
            <SwiperSlide key={round.id} className="round-slide">
              <div
                className="round-viewport"
                style={{
                  height: containerHeight ? `${containerHeight}px` : "auto",
                  transition: isDragging ? "none" : "height 240ms ease",
                }}
              >
                <div
                  className="round"
                  ref={(el) => (slideContentRefs.current[idx] = el)}
                >
                  <div className="round-list">
                    <RoundPhaseWrapper
                      title={round.title}
                      matches={round.matches || []}
                      onSelectWinner={onSelectWinner}
                      className={round.className}
                      groupingStrategy={round.strategy}
                    />
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}